/**
 * Tax → Harvest and Gain panels, ported from views/tax.ejs.
 *
 * Both are the same idea from opposite ends: the Harvest tab lists open lots that could be SOLD at a loss
 * (with the wash-sale verdict for each), the Gain tab lists lots that could be sold to fill a gain ceiling.
 * The tables are the point of the page — the cards and meters above them are only a picture of these rows — so
 * they are the same `Table` component with the same columns and the same row hooks (`tr.harvest-row`,
 * `tr.gain-row`) that the stylesheet and the browser suite address.
 *
 * The panes' shape is the EJS page's, section by section: `.hv-cards`, the five-column balance with its status
 * sentence, the plan-a-sale line, the harvest table, the netting ladder, the hidden/unpriced/basis notes and
 * the Replacement windows block — and, on the gain pane, the 0%-room notice, its cards, the `Fits` column and
 * the near-long-term table. The SPA had built its own meter-and-bars version of the same figures, which read
 * as a different page.
 */
import { useEffect, useState } from 'react';
import { InfoTip } from '../../components/InfoTip';
import { Table } from '../../components/Table';
import { Tabs } from '../../components/Tabs';
import { Card, Cards } from '../../components/Card';
/**
 * The netting ladder — the SAME script the EJS page loads (`<script src="/harvest-ladder.js">`), loaded the same
 * way rather than re-implemented: it is a UMD module that registers `PFHarvestLadder` on the global when there is
 * no CommonJS, and bundling it would give the SPA a second copy of the IRS order. `useLadder` injects the script
 * once and resolves the global; until it arrives the after-harvest cells read "—", which is also what they read
 * with nothing selected.
 */
type LadderResult = {
  buckets: { key: string; amount: number; saved: number }[];
  carriesForward: number;
  savedFederal: number;
  ordinaryRoom: number;
};
type LadderApi = { ladder: (input: Record<string, number>) => LadderResult };

let ladderPromise: Promise<LadderApi | null> | null = null;
function useLadder(): LadderApi | null {
  const [api, setApi] = useState<LadderApi | null>((window as unknown as { PFHarvestLadder?: LadderApi }).PFHarvestLadder ?? null);
  useEffect(() => {
    if (api) return;
    const w = window as unknown as { PFHarvestLadder?: LadderApi };
    if (!ladderPromise) {
      ladderPromise = new Promise((resolve) => {
        const el = document.createElement('script');
        el.src = '/harvest-ladder.js';
        el.async = true;
        el.onload = () => resolve(w.PFHarvestLadder ?? null);
        el.onerror = () => resolve(null);
        document.head.appendChild(el);
      });
    }
    ladderPromise.then(setApi);
  }, [api]);
  return api;
}
import { abbrM, money, nfmt } from '../../format';
import { lbl } from '../../labels';

export type HarvestRow = {
  lotId: number | string;
  ticker: string;
  listName?: string;
  shares: number;
  basis: number | null;
  price: number | null;
  priceDate?: string;
  priceStale?: boolean;
  value: number | null;
  loss: number | null;
  term?: string;
  daysToLong?: number | null;
  washTier?: string;
  washText?: string;
  priorWashed?: boolean;
  priced?: boolean;
  recognizedLoss?: number | null;
  deferredLoss?: number | null;
  disallowedLoss?: number | null;
};

export type WashWindow = {
  ticker: string;
  date?: string;
  soldDate?: string;
  loss?: number;
  closeDate?: string;
  open?: boolean;
  rebuys?: { quantity?: number; date?: string; listType?: string }[];
  priorBuys?: { quantity?: number; date?: string }[];
};

export type HarvestPanel = {
  saleDate?: string;
  minLoss?: number;
  basisMethod?: string;
  totalLoss?: number;
  recognizedLoss?: number;
  deferredLoss?: number;
  disallowedLoss?: number;
  unpricedCount?: number;
  pricedCount?: number;
  washedCount?: number;
  hiddenCount?: number;
  balance?: { stLoss?: number; ltLoss?: number; unknownLoss?: number; total?: number };
  disclosure?: string;
  rows: HarvestRow[];
  /** The other half of the wash-sale rule: losses already realized whose 61-day window is still open. */
  windows?: { rows: WashWindow[]; days?: number; openCount?: number; saleDate?: string };
};

export type GainRow = {
  lotId: number | string;
  ticker: string;
  listName?: string;
  shares: number;
  basis?: number | null;
  costPerShare?: number | null;
  price: number | null;
  priceDate?: string;
  gain: number | null;
  term?: string;
  daysToLong?: number | null;
  priorWashed?: boolean;
  priced?: boolean;
  fit?: string;
  partial?: { shares?: number } | number | null;
  fits?: string | boolean;
};

export type GainPanel = {
  saleDate?: string;
  totalGain?: number;
  headroom?: {
    room?: number;
    ceiling?: number;
    filled?: { ordinary?: number; dividends?: number; ltGains?: number };
  } | null;
  ceiling?: number;
  hasProfile?: boolean;
  unpricedCount?: number;
  disclosure?: string;
  rows: GainRow[];
  nearLong?: GainRow[];
};

const abs = (n: number | null | undefined) => money(Math.abs(n ?? 0));

export function HarvestAndGain({
  harvest,
  gain,
  year,
  stTotal,
  ltTotal,
  targetSt,
  targetLt,
  marg,
  lossDeducted,
  lossCarryforward,
}: {
  harvest: HarvestPanel | null;
  gain: GainPanel | null;
  year: number;
  /** The year's realized totals — the "Gain to offset" column of the balance, which is what loss has to offset. */
  stTotal: number;
  ltTotal: number;
  /** How much loss the year can still absorb, and the marginal rates the ladder prices it at. */
  targetSt: number;
  targetLt: number;
  marg: { ordRate: number; ltRate: number; niit: number };
  lossDeducted: number;
  /** What the year's capital losses leave to carry forward — the EJS card's figure, absent without a profile. */
  lossCarryforward?: number | null;
}) {
  const [tab, setTab] = useState<'loss' | 'gain'>('loss');
  // Selected lots, by lotId. Clicking a row plans its sale — the EJS page does the same (`data-sel`), and the
  // meter and the after-harvest cells are the answer to "how much more do I tick?".
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  if (!harvest && !gain) return null;

  const bal = harvest?.balance || {};
  const hv = harvest?.rows || [];
  const gv = gain?.rows || [];
  const nearLong = gain?.nearLong || [];
  const wv = harvest?.windows || { rows: [], days: 35, openCount: 0 };

  const selectedLoss = hv.reduce((sum, r) => (picked.has(String(r.lotId)) && r.loss != null ? sum + Math.abs(r.loss) : sum), 0);
  // The plan is the SERVER's counterfactual (POST /tax/gain-plan re-prices the year with the selected lots), not
  // a sum the browser does: the tax on a selection is not the sum of the tax on each lot.
  const [plan, setPlan] = useState<{ plan: unknown } | null>(null);
  const ids = [...picked].join(',');
  useEffect(() => {
    if (!ids) {
      setPlan(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/v1/tax/gain-plan?year=${year}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ lots: ids }),
    })
      .then((r) => r.json())
      .then((j) => !cancelled && setPlan(j?.data ?? null))
      .catch(() => !cancelled && setPlan(null));
    return () => {
      cancelled = true;
    };
  }, [ids, year]);

  /** The plan's lines, in the EJS page's words: where the gain lands and what it costs federally. */
  const renderPlan = (p: {
    totalGain?: number;
    atZero?: number;
    atFifteen?: number;
    federalTax?: number;
    niit?: number;
    state?: string | null;
    stateTax?: number;
  } | null) =>
    !p ? (
      <div>Set your tax profile to see what the selection would cost.</div>
    ) : (
      <>
        <div>
          Selected {money(p.totalGain ?? 0)} of gain: <strong>{money(p.atZero ?? 0)}</strong> at 0%
          {(p.atFifteen ?? 0) > 0 ? (
            <>
              , <strong>{money(p.atFifteen ?? 0)}</strong> spilling into 15%
            </>
          ) : (
            ', none spilling into 15%'
          )}
          .
        </div>
        <div>
          Federal tax on the selection: <strong>{money(p.federalTax ?? 0)}</strong>
          {(p.niit ?? 0) > 0 ? <> (including {money(p.niit ?? 0)} NIIT)</> : null}
        </div>
        {p.state ? (
          <div>
            {p.state} tax on the selection: <strong>{money(p.stateTax ?? 0)}</strong> — shown beside the federal
            figure, never netted with it.
          </div>
        ) : null}
      </>
    );

  const ladderApi = useLadder();
  const ladder = ladderApi?.ladder({
    selectedLoss,
    stGains: stTotal,
    ltGains: ltTotal,
    ordRate: marg.ordRate,
    ltRate: marg.ltRate,
    niit: marg.niit,
    lossDeducted,
  });
  const appliedTo = (key: string) => ladder?.buckets.find((b) => b.key === key)?.amount || 0;
  const target = Math.max(0, targetSt) + Math.max(0, targetLt);
  const remaining = Math.max(0, target - selectedLoss);
  const coveredPct = target > 0 ? Math.min(100, (100 * selectedLoss) / target) : 0;

  /** The balance's status sentence, as the EJS page words it. */
  const balanceStatus = (gainN: number, lossN: number) => {
    const gap = Math.max(0, gainN - lossN);
    const spare = Math.max(0, lossN - gainN);
    const ratio = gainN > 0 ? lossN / gainN : 0;
    if (gainN <= 0 && lossN <= 0) return 'nothing to offset or harvest';
    if (gainN <= 0) return 'no gain — the loss carries forward';
    if (gap <= 0) return 'covered ' + ratio.toFixed(1) + '×' + (spare > 0 ? ' · ' + money(spare) + ' spare' : '');
    return (
      <>
        still needed <strong>{money(gap)}</strong>
      </>
    );
  };

  const balRows = (['st', 'lt'] as const).map((k) => {
    const gainN = k === 'st' ? stTotal : ltTotal;
    const lossN = (k === 'st' ? bal.stLoss : bal.ltLoss) || 0;
    const after = appliedTo(k);
    return {
      key: k,
      rowAttrs: { 'data-term': k },
      cells: [
        <strong key="c">{k === 'st' ? 'Short-term' : 'Long-term'}</strong>,
        { content: money(gainN), className: gainN > 0 ? 'pos' : 'muted' },
        { content: money(lossN), className: lossN > 0 ? 'neg' : 'muted' },
        {
          content: picked.size && ladder ? money(Math.max(0, gainN - after)) : '—',
          className: 'muted',
          attrs: { id: 'hv-after-val-' + k },
        },
        { content: balanceStatus(gainN, lossN), className: 'muted' },
      ],
    };
  });

  const harvestCards = (
    <Cards className="hv-cards">
      <Card
        label="Harvestable"
        valueTag="strong"
        valueClass={harvest?.recognizedLoss != null && harvest.recognizedLoss < 0 ? 'neg' : ''}
        title={money(harvest?.recognizedLoss ?? 0)}
        value={abbrM(harvest?.recognizedLoss ?? 0)}
      />
      {/* views/tax.ejs's own rule: a net gain is 'pos', a net loss 'neg' — and ZERO is a gain side figure there,
          so a flat year keeps the positive ink instead of losing its class. */}
      <Card label={`Net short-term ${year}`} valueTag="strong" valueClass={stTotal < 0 ? 'neg' : 'pos'} value={abbrM(stTotal)} />
      <Card label={`Net long-term ${year}`} valueTag="strong" valueClass={ltTotal < 0 ? 'neg' : 'pos'} value={abbrM(ltTotal)} />
      {/* views/tax.ejs appends these two only when the tax matrix exists: without wages there is no
          $3,000 offset and nothing carried forward to show. */}
      {lossCarryforward != null ? (
        <>
          <Card label="$3,000 offset used" valueTag="strong" value={abbrM(lossDeducted || 0)} />
          <Card label="Carries forward" valueTag="strong" value={abbrM(lossCarryforward)} />
        </>
      ) : null}
    </Cards>
  );

  const gainCards = (
    <Cards className="hv-cards">
      {gain?.hasProfile ? (
        <>
          <Card label={`0% room left ${year}`} valueTag="strong" title={money(gain.headroom?.room ?? 0)} value={money(gain.headroom?.room ?? 0)} />
          <Card label="0% ceiling" valueTag="strong" value={money(gain.headroom?.ceiling ?? 0)} />
          <Card label="Ordinary income fills" valueTag="strong" value={money(gain.headroom?.filled?.ordinary ?? 0)} />
          <Card label="Dividends so far" valueTag="strong" value={money(gain.headroom?.filled?.dividends ?? 0)} />
          <Card label="Realized LT gains" valueTag="strong" value={money(gain.headroom?.filled?.ltGains ?? 0)} />
        </>
      ) : (
        <Card label="0% tier ceiling" valueTag="strong" value={money(gain?.ceiling ?? 0)} />
      )}
    </Cards>
  );

  return (
    <section className="panel-card" id="harvest-panel">
      <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        Harvest
        {/* views/tax.ejs gives this panel its widest bubble (420px): it carries both panels' disclosures, so it is the one tip that must not wrap into a narrow column. */}
        <InfoTip anchor="inline" place="below-left" icon="tax" width={420} padX={10}>
          <strong>Estimate, not tax advice</strong> · open lots in your taxable lists
          {harvest?.saleDate ? ` · priced as of ${harvest.saleDate}` : ''}
          {harvest?.disclosure ? <div style={{ marginTop: 6 }}>{harvest.disclosure}</div> : null}
          {gain?.disclosure ? <div style={{ marginTop: 6 }}>{gain.disclosure}</div> : null}
        </InfoTip>
      </h2>

      <Tabs
        id="harvest-tabs"
        variant="segmented"
        raised
        ariaLabel="Harvest view"
        active={tab}
        style={{ margin: '8px 0 6px' }}
        onChange={(k) => setTab(k as 'loss' | 'gain')}
        items={[
          { key: 'loss', label: 'Loss', pane: 'harvest-pane-loss' },
          { key: 'gain', label: 'Gain', pane: 'harvest-pane-gain' },
        ]}
      />

      <div id="harvest-pane-loss" role="tabpanel" hidden={tab !== 'loss'}>
        {harvestCards}

        {/* The balance, as the EJS page draws it: one row per character, with the status spelled out. */}
        <Table
          expandable={false}
          tableId="harvest-balance"
          tableClass=""
          style={{ marginTop: 14 }}
          columns={[
            { label: 'Character' },
            { label: 'Gain to offset', num: true },
            { label: 'Harvestable loss', num: true },
            { label: 'After harvest', num: true },
            { label: 'Status' },
          ]}
          rows={balRows}
        />

        {/* The meter answers "how much more do I tick?" — hidden until something is selected, as on the EJS page. */}
        <p className="tax-small" id="harvest-meter" style={{ display: picked.size ? 'block' : 'none', margin: '8px 0 0' }}>
          <strong>Selected</strong>{' '}
          <span id="harvest-meter-text">
            {coveredPct.toFixed(0)}% of the {money(target)} target · {money(remaining)} to go ·{' '}
            {money(ladder?.savedFederal ?? 0)} saved
          </span>
        </p>
        <p className="muted tax-small" id="harvest-selected" style={{ margin: '8px 0 0' }}>
          {picked.size
            ? `${nfmt(picked.size)} lot${picked.size === 1 ? '' : 's'} selected · ${money(selectedLoss)} of loss`
            : 'Click a row to plan a sale.'}
        </p>
        {(bal.unknownLoss || 0) > 0 ? (
          <p className="muted tax-tiny" style={{ margin: '4px 0 0' }}>
            {money(bal.unknownLoss)} of harvestable loss sits in lots whose term is unknown — not in either row.
          </p>
        ) : null}

        {hv.length === 0 ? (
          <p className="muted tax-body" style={{ margin: '10px 0 0' }}>
            No open lot in your taxable lists is at a loss today.
          </p>
        ) : (
          <>
            {harvest?.deferredLoss || harvest?.disallowedLoss ? (
              <p className="muted tax-small" style={{ margin: '8px 0 0' }}>
                Selling every candidate today: {abs(harvest?.totalLoss)} of loss, less{' '}
                {harvest?.deferredLoss && harvest?.disallowedLoss ? (
                  <>
                    {abs(harvest.deferredLoss)} deferred and {abs(harvest.disallowedLoss)} disallowed by repurchases
                    in your lists
                  </>
                ) : harvest?.disallowedLoss ? (
                  <>{abs(harvest.disallowedLoss)} disallowed by a rebuy in a tax-deferred account</>
                ) : (
                  <>{abs(harvest?.deferredLoss)} deferred by a repurchase in your lists</>
                )}{' '}
                — {money(harvest?.recognizedLoss ?? 0)} recognized now.
              </p>
            ) : (
              <p className="muted tax-small" style={{ margin: '8px 0 0' }}>
                Selling every candidate today: {abs(harvest?.totalLoss)} of loss, none of it washed by a repurchase
                in your lists.
              </p>
            )}

            <Table
              expandable={false}
              alignRows
              tableId="harvest-table"
              tableClass=""
              style={{ marginTop: 12 }}
              onClickRow={(k) => toggle(k)}
              columns={[
                { label: lbl('Ticker') },
                { label: lbl('Shares'), num: true },
                { label: lbl('Cost Basis'), num: true },
                { label: lbl('Price'), num: true },
                { label: lbl('Value'), num: true },
                { label: lbl('Loss'), num: true },
                { label: lbl('Term') },
                { label: lbl('Wash sale') },
              ]}
              rows={hv.map((r) => {
                const minor = r.priced && r.loss != null && Math.abs(r.loss) <= (harvest?.minLoss ?? 0);
                const bad = r.washTier === 'disallowed' || r.washTier === 'mixed';
                const washCls = bad ? 'neg' : r.washTier === 'clean' ? 'muted' : '';
                const isPicked = picked.has(String(r.lotId));
                return {
                  key: String(r.lotId),
                  rowClass: 'harvest-row' + (isPicked ? ' selected' : ''),
                  rowAttrs: {
                    'data-lot': String(r.lotId),
                    'data-shares': r.shares,
                    'data-loss': r.loss == null ? '' : r.loss,
                    'data-recognized': r.recognizedLoss == null ? '' : r.recognizedLoss,
                    'data-deferred': r.deferredLoss == null ? '' : r.deferredLoss,
                    'data-disallowed': r.disallowedLoss == null ? '' : r.disallowedLoss,
                    'data-sel': isPicked ? '1' : '0',
                    ...(minor ? { 'data-minor': '1', style: 'display: none;' } : {}),
                  },
                  cells: [
                    <>
                      <strong>{r.ticker}</strong>
                      <span className="muted tax-tiny" style={{ display: 'block' }}>{r.listName || ''}</span>
                    </>,
                    nfmt(r.shares),
                    money(r.basis ?? 0),
                    !r.priced ? (
                      <span className="muted">—</span>
                    ) : (
                      <>
                        {money(r.price ?? 0)}
                        {r.priceStale && r.priceDate ? (
                          <span className="muted tax-tiny" style={{ display: 'block' }}>{r.priceDate}</span>
                        ) : null}
                      </>
                    ),
                    r.value == null ? <span className="muted">—</span> : money(r.value),
                    r.loss == null ? <span className="muted">unknown</span> : <span className="neg">{money(r.loss)}</span>,
                    <>
                      <span className="tag">{r.term === 'long' ? 'Long' : r.term === 'short' ? 'Short' : 'Unknown'}</span>
                      {r.term === 'short' && r.daysToLong != null && r.daysToLong <= 7 ? (
                        <span className="muted tax-tiny" style={{ display: 'block' }}>long in {r.daysToLong}d</span>
                      ) : null}
                    </>,
                    <>
                      <span className={washCls} style={r.washTier === 'deferred' ? { color: '#d9a03f' } : undefined}>
                        {r.washText || '—'}
                      </span>
                      {r.priorWashed ? (
                        <span className="muted tax-tiny" style={{ display: 'block' }}>
                          basis not wash-adjusted — true loss larger, term may differ
                        </span>
                      ) : null}
                    </>,
                  ],
                };
              })}
            />

            {/* The ladder is visible even with nothing selected: it states the year's position, so the
                netting order is not something a reader only discovers after clicking a row. */}
            <div className="tax-body" id="harvest-ladder" style={{ margin: '12px 0 0', lineHeight: 1.6 }}>
              {stTotal + ltTotal <= 0 ? (
                <div className="muted">
                  {`No net realized gain for ${year} — every dollar of loss harvested today carries forward to ${year + 1}.`}
                </div>
              ) : (
                <>
                  <div>
                    Realized gains still to offset: <strong>short-term {money(stTotal)}</strong> ·{' '}
                    <strong>long-term {money(ltTotal)}</strong>
                  </div>
                  <div className="muted">
                    {/* One text node either side of the strong, as views/tax.ejs writes it: the partial emits the
                        whole sentence as a single run, and node-by-node extraction reads a split run as different
                        text. */}
                    Harvest <strong>{money(stTotal + ltTotal)}</strong>
                    {` of loss to zero the year, then up to ${money(
                      Math.max(0, 3000 - (lossDeducted || 0)),
                    )} against ordinary income — beyond that it carries forward to ${year + 1}.`}
                  </div>
                </>
              )}
            </div>
            {(harvest?.hiddenCount ?? 0) > 0 ? (
              <p className="muted tax-small" style={{ margin: '6px 0 0' }}>
                {harvest?.hiddenCount} lot{harvest?.hiddenCount === 1 ? '' : 's'} under ${harvest?.minLoss} hidden —{' '}
                <a href="#" id="harvest-show-minor" onClick={(e) => e.preventDefault()}>show</a>
              </p>
            ) : null}
            {(harvest?.unpricedCount ?? 0) > 0 ? (
              <p className="muted tax-small" style={{ margin: '6px 0 0' }}>
                {harvest?.unpricedCount} unpriced lot{harvest?.unpricedCount === 1 ? '' : 's'} excluded from the
                totals above — no price to measure a loss against.
              </p>
            ) : null}
            <p className="muted tax-small" style={{ margin: '6px 0 0' }}>
              Lot order shown per your {harvest?.basisMethod} setting — confirm at the broker (SPECIFIC ID must be
              elected there).
            </p>
          </>
        )}

        {/* Replacement windows: the other half of the rule. The table above says what selling TODAY would do;
            this says what the losses already taken still require — no repurchase of the same ticker before the
            window closes, or the loss is disallowed. Not year-scoped: a December sale's window closes in January. */}
        <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <h3 className="tax-h3" style={{ margin: '0 0 4px' }}>Replacement windows</h3>
          <p className="muted tax-small" style={{ margin: '0 0 8px' }}>
            A realized loss stands only if the same ticker was not bought within 30 days either side of the sale.
            The scan reads the same lists the disclosure above names — taxable and tax-deferred.
          </p>
          {!wv.rows.length ? (
            <p className="muted tax-small" style={{ margin: 0 }}>
              No loss realized in the last {wv.days} days, and no window is open.
            </p>
          ) : (
            <Table
              expandable={false}
              tableId="wash-windows"
              tableClass=""
              columns={[
                { label: lbl('Ticker') },
                { label: lbl('Sold') },
                { label: lbl('Loss'), num: true },
                { label: lbl('Window closes') },
                { label: lbl('Wash sale') },
              ]}
              rows={wv.rows.map((w, i) => {
                const rebuy = (w.rebuys || [])[0] || null;
                let wash: React.ReactNode;
                if (rebuy) {
                  const deferred = rebuy.listType === 'tax_deferred';
                  wash = (
                    <>
                      <span className="neg">
                        Rebought {nfmt(rebuy.quantity ?? 0)} sh on {rebuy.date}
                        {deferred ? ' in a tax-deferred list' : ''} — the loss is disallowed
                        {deferred ? ', with no basis adjustment' : ''}
                      </span>
                      {(w.rebuys || []).length > 1 ? (
                        <span className="muted tax-tiny" style={{ display: 'block' }}>
                          and {(w.rebuys || []).length - 1} more repurchase
                          {(w.rebuys || []).length - 1 === 1 ? '' : 's'} inside the window
                        </span>
                      ) : null}
                    </>
                  );
                } else if (w.open) {
                  wash = <span style={{ color: '#d9a03f' }}>Open — do not rebuy {w.ticker} before {w.closeDate}</span>;
                } else {
                  wash = <span className="muted">Closed — nothing rebought inside the window</span>;
                }
                return {
                  key: String(i),
                  cells: [
                    <strong key="t">{w.ticker}</strong>,
                    String(w.soldDate ?? w.date ?? '—'),
                    <span key="l" className="neg">{money(Math.abs(w.loss ?? 0))}</span>,
                    String(w.closeDate ?? '—'),
                    wash,
                  ],
                };
              })}
            />
          )}
        </div>
      </div>

      <div id="harvest-pane-gain" role="tabpanel" hidden={tab !== 'gain'}>
        {!gain ? (
          <p className="muted tax-body">No gain view available.</p>
        ) : (
          <>
            {!gain.hasProfile ? (
              <p className="tax-notice">
                Set your tax profile to see how much 0%-rate room is left — without an income figure there is
                nothing to measure the tier against.
              </p>
            ) : (gain.headroom?.room ?? 0) <= 0 ? (
              <p className="tax-notice">
                No 0%-rate room left for {year} — a realized gain now lands in the 15% tier.
              </p>
            ) : (
              <p className="tax-notice">
                {money(gain.headroom?.room ?? 0)} of 0%-rate room left for {year} — gains realized inside it are
                taxed at 0%.
              </p>
            )}

            {gainCards}

            {gv.length === 0 ? (
              <p className="muted tax-body" style={{ margin: '10px 0 0' }}>
                No open lot in your taxable lists is at a long-term gain today.
              </p>
            ) : (
              <>
                <Table
                  expandable={false}
                  alignRows
                  tableId="gain-table"
                  tableClass=""
                  style={{ marginTop: 12 }}
                  onClickRow={(k) => toggle(k)}
                  columns={[
                    { label: lbl('Ticker') },
                    { label: lbl('Shares'), num: true },
                    { label: lbl('Cost/share'), num: true },
                    { label: lbl('Price'), num: true },
                    { label: lbl('Gain'), num: true },
                    { label: lbl('Term') },
                    { label: lbl('Fits') },
                  ]}
                  rows={gv.map((r) => {
                    const isPicked = picked.has(String(r.lotId));
                    let fit: React.ReactNode;
                    if (r.fit === 'fits') fit = <span className="pos">fits</span>;
                    else if (r.fit === 'partial')
                      fit = (
                        <>
                          <span style={{ color: '#d9a03f' }}>partial</span>
                          <span className="muted tax-tiny" style={{ display: 'block' }}>
                            sell {nfmt(typeof r.partial === 'number' ? r.partial : r.partial?.shares ?? 0)} of{' '}
                            {nfmt(r.shares)} sh — {money(gain.headroom?.room ?? 0)} of room
                          </span>
                        </>
                      );
                    else if (r.fit === 'over') fit = <span className="neg">over</span>;
                    else fit = <span className="muted">—</span>;
                    return {
                      key: String(r.lotId),
                      rowClass: 'gain-row' + (isPicked ? ' selected' : ''),
                      rowAttrs: {
                        'data-lot': String(r.lotId),
                        'data-gain': r.gain == null ? '' : r.gain,
                        'data-shares': r.shares,
                        'data-sel': isPicked ? '1' : '0',
                        style: 'cursor: pointer',
                      },
                      cells: [
                        <>
                          <strong>{r.ticker}</strong>
                          <span className="muted tax-tiny" style={{ display: 'block' }}>{r.listName || ''}</span>
                        </>,
                        nfmt(r.shares),
                        money(r.costPerShare ?? 0),
                        !r.priced ? (
                          <span className="muted">—</span>
                        ) : (
                          <>
                            {money(r.price ?? 0)}
                            {r.priceDate ? (
                              <span className="muted tax-tiny" style={{ display: 'block' }}>{r.priceDate}</span>
                            ) : null}
                          </>
                        ),
                        r.gain == null ? <span className="muted">unknown</span> : <span className="pos">{money(r.gain)}</span>,
                        <span className="tag">Long</span>,
                        <>
                          {fit}
                          {r.priorWashed ? (
                            <span className="muted tax-tiny" style={{ display: 'block' }}>
                              basis not wash-adjusted — true gain is larger
                            </span>
                          ) : null}
                        </>,
                      ],
                    };
                  })}
                />
                <p className="muted tax-small" id="gain-selected" style={{ margin: '10px 0 0' }}>
                  {picked.size ? `${nfmt(picked.size)} lot${picked.size === 1 ? '' : 's'} selected` : 'Click a row to plan a gain harvest.'}
                </p>
                <div className="tax-body" id="gain-plan" style={{ margin: '8px 0 0', lineHeight: 1.6 }}>
                  {picked.size === 0 ? '' : plan?.plan ? renderPlan(plan.plan as never) : 'Planning the selection…'}
                </div>

                {nearLong.length ? (
                  <>
                    <p className="muted tax-small" style={{ margin: '10px 0 0' }}>
                      Not candidates yet — a short-term sale is taxed at ordinary rates, so these are information
                      only:
                    </p>
                    <Table
                      expandable={false}
                      tableId="gain-nearlong-table"
                      tableClass=""
                      style={{ marginTop: 6 }}
                      columns={[
                        { label: lbl('Ticker') },
                        { label: lbl('Shares'), num: true },
                        { label: lbl('Gain'), num: true },
                        { label: lbl('Term') },
                      ]}
                      rows={nearLong.map((r) => ({
                        key: String(r.lotId),
                        rowClass: 'gain-nearlong-row',
                        rowAttrs: { style: 'opacity: 0.6' },
                        cells: [
                          <>
                            <strong>{r.ticker}</strong>
                            <span className="muted tax-tiny" style={{ display: 'block' }}>{r.listName || ''}</span>
                          </>,
                          nfmt(r.shares),
                          <span key="g" className="pos">{money(r.gain ?? 0)}</span>,
                          <>
                            <span className="tag">Short</span>
                            <span className="muted tax-tiny" style={{ display: 'block' }}>
                              long in {r.daysToLong} days
                            </span>
                          </>,
                        ],
                      }))}
                    />
                  </>
                ) : null}

                {(gain.unpricedCount ?? 0) > 0 ? (
                  <p className="muted tax-small" style={{ margin: '6px 0 0' }}>
                    {gain.unpricedCount} unpriced lot{gain.unpricedCount === 1 ? '' : 's'} listed with an unknown
                    gain — no price to measure against, so {gain.unpricedCount === 1 ? 'it is' : 'they are'} sorted
                    last and cannot be planned.
                  </p>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
